export interface EmployeeUploadFilterState {
  establishmentType: string;
  company: string;
  associateCompany: string;
  location: string;
  year: string;
  month: string;
  // Additional CLRA filters
  employer: string;
  employerLocation: string;
  subContractor: string;
  subContractorLocation: string;
}

export interface EmployeeUploadSearchConfig {
  searchInput: string;
  activeSort: string;
  sortType: 'asc' | 'desc';
  page: number;
  rowsPerPage: number;
}

export interface EmployeeUploadConfig {
  monthFieldName?: 'month_eq' | 'wage_month_eq';
  searchFieldName: 'employee_code_cont' | 'name_cont';
}

export const INITIAL_EMPLOYEE_UPLOAD_FILTER_STATE: EmployeeUploadFilterState = {
  establishmentType: '',
  company: '',
  associateCompany: '',
  location: '',
  year: '',
  month: '',
  // Additional CLRA filters
  employer: '',
  employerLocation: '',
  subContractor: '',
  subContractorLocation: ''
};

export const INITIAL_EMPLOYEE_UPLOAD_SEARCH_CONFIG: EmployeeUploadSearchConfig = {
  searchInput: '',
  activeSort: 'created_at',
  sortType: 'desc',
  page: 0,
  rowsPerPage: 10
};

export const EMPLOYEE_UPLOAD_MONTH_LIST = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export const generateEmployeeUploadYearsList = (): number[] => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= 1950; i--) {
    years.push(i);
  }
  return years;
};

export const getEmployeeUploadResetFields = (field: keyof EmployeeUploadFilterState): Partial<EmployeeUploadFilterState> => {
  switch (field) {
    case 'establishmentType':
      return {
        company: '',
        associateCompany: '',
        location: '',
        employer: '',
        employerLocation: '',
        subContractor: '',
        subContractorLocation: ''
      };
    case 'company':
      return {
        associateCompany: '',
        location: '',
        employer: '',
        employerLocation: '',
        subContractor: '',
        subContractorLocation: ''
      };
    case 'associateCompany':
      return {
        location: '',
        employer: '',
        employerLocation: '',
        subContractor: '',
        subContractorLocation: ''
      };
    case 'location':
      return {
        employer: '',
        employerLocation: '',
        subContractor: '',
        subContractorLocation: ''
      };
    case 'year':
      return {
        month: ''
      };
    case 'employer':
      return {
        employerLocation: '',
        subContractor: '',
        subContractorLocation: ''
      };
    case 'employerLocation':
      return {
        subContractor: '',
        subContractorLocation: ''
      };
    case 'subContractor':
      return {
        subContractorLocation: ''
      };
    default:
      return {};
  }
};

export const buildEmployeeUploadQueryPayload = (
  filters: EmployeeUploadFilterState,
  searchConfig: EmployeeUploadSearchConfig,
  config: EmployeeUploadConfig,
  overrides: Partial<any> = {},
  sortField?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  const queryFilters: any = {};

  // Build filters
  if (filters.establishmentType) {
    queryFilters.establishment_type_eq = filters.establishmentType;
  }
  if (filters.company) {
    queryFilters.company_id_eq = filters.company;
  }
  if (filters.associateCompany) {
    queryFilters.associate_company_id_eq = filters.associateCompany;
  }
  if (filters.location) {
    queryFilters.location_id_eq = filters.location.split('^')[0];
  }
  if (filters.year) {
    queryFilters.year_eq = filters.year;
  }
  if (filters.month && config.monthFieldName) {
    queryFilters[config.monthFieldName] = filters.month;
  }

  // Add CLRA additional filters
  if (filters.employer) {
    queryFilters.employer_id_eq = filters.employer;
  }
  if (filters.employerLocation) {
    queryFilters.employer_location_id_eq = filters.employerLocation;
  }
  if (filters.subContractor) {
    queryFilters.sub_contractor_id_eq = filters.subContractor;
  }
  if (filters.subContractorLocation) {
    queryFilters.sub_contractor_location_id_eq = filters.subContractorLocation;
  }

  // Add search
  if (searchConfig.searchInput) {
    queryFilters[config.searchFieldName] = searchConfig.searchInput;
  }

  // Build sorts
  const sorts = sortField && sortDirection
    ? [`${sortField} ${sortDirection}`]
    : [`${searchConfig.activeSort} ${searchConfig.sortType}`];

  return {
    filters: { ...queryFilters, ...overrides },
    per: searchConfig.rowsPerPage,
    page: searchConfig.page + 1,
    sorts,
    includeCentral: true,
    include: {
      company_employer_sub_contractor: {
        only: ['id', 'company_employer_sub_contractor_code']
      }
    }
  };
};

export const sortEmployeeUploadCompaniesByName = <T extends { name?: string }>(list: T[]): T[] => {
  return (list || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

export const getEmployeeUploadCompanyNameById = (id: string, companies: { id: string; name: string; }[]) => {
  for (let i = 0; i < companies.length; i++) {
    if (companies[i].id === id) {
      return companies[i].name;
    }
  }
  return '';
};

export const getEmployeeUploadAssocCompanyNameById = (id: string, associateCompanies: { id: string; name: string; }[]) => {
  for (let i = 0; i < associateCompanies.length; i++) {
    if (associateCompanies[i].id === id) {
      return associateCompanies[i].name;
    }
  }
  return '';
};

export const getEmployeeUploadLocationById = (id: string, locations: any[]) => {
  for (let i = 0; i < locations.length; i++) {
    if (locations[i].location_id === id) {
      return locations[i].name;
    }
  }
  return '';
};

export const formatEmployeeUploadDate = (dateString: string | number | Date) => {
  if (dateString === null || dateString === undefined || dateString === '') {
    return '-';
  }

  // Handle plain date-only strings like "yyyy-mm-dd" or "yyyy/mm/dd" or "yyyy-mm-ddT..."
  // without going through `new Date()` to avoid timezone shifts.
  if (typeof dateString === 'string') {
    const ymdMatch = dateString.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (ymdMatch) {
      const [, y, m, d] = ymdMatch;
      return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }

    // Already dd/mm/yyyy or dd-mm-yyyy — normalize separators
    const dmyMatch = dateString.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmyMatch) {
      const [, d, m, y] = dmyMatch;
      return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }
  }

  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-GB', options);
};

export const formatEmployeeUploadTime = (timeString: string | number | Date) => {
  if (!timeString) {
    return '-';
  }

  const date = new Date(timeString);

  if (isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleTimeString('en-GB');
};